import React, { useEffect, useMemo, useState, FunctionComponent } from 'react'

// Extend the Window interface to include mapRegion and gtag
declare global {
    interface Window {
        mapRegion: 'na' | 'eu' | 'apac';
        mapLanguage: 'en' | 'de';
        locations: any[];
        mapIntro: string;
        gtag?: (...args: any[]) => void;
    }
}

import { createRoot } from 'react-dom/client';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import classNames from 'classnames';

const geoUrl = "/wp-content/themes/SavillsIM/world.json";

// Utility function to decode HTML entities
const decodeHtmlEntities = (text: string): string => {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
};

interface Props {
    location: any;
    isActive: boolean;
    onClick: (location: any) => void;
}

export const CustomMarker: FunctionComponent<Props> = ({
    location,
    isActive,
    onClick,
}) => {
    const position = [
        parseFloat(location.latLong.long),
        parseFloat(location.latLong.lat)
    ];

    const handleClick = () => {
        if (onClick) onClick(location);
    };

    return (
      <Marker coordinates={[position[0], position[1]]} onClick={handleClick} className={classNames({ 'pulse-current !z-30 border border-white': isActive }, { 'text-savillsim-teal': location.location_type === 'example_asset', 'text-savillsim-orange': location.location_type === 'savills_office' }, 'z-20')}>
        {isActive && (<circle r={1} fill="currentColor" className="pointer-events-none">
            <animate attributeName="r" from="1.5" to="5" dur="1.5s" begin="0s" repeatCount="indefinite"/>
            <animate attributeName="opacity" from="1" to="0" dur="1.5s" begin="0s" repeatCount="indefinite"/>
        </circle>)}
        <circle r={1} fill="currentColor" stroke="white" strokeWidth={isActive ? 0.25 : 0} className="hover:scale-150 transition">
        </circle>
      </Marker>
    );
};

const App = () => {
    const [introText, setIntroText] = useState(window.mapIntro);
    const [filter, setFilter] = useState('all');
    const [locations, setLocations] = useState(window.locations);

    const [activeLocationIndex, setActiveLocationIndex] = useState(0);

    const [filteredLocations, setFilteredLocations] = useState(locations);

    const [mapLanguage, setMapLanguage] = useState(window.mapLanguage);

    console.log(`Map language: ${mapLanguage}`);

    const localeStrings = {
        en: {
            all: 'All',
            example_asset: 'Example Assets',
            savills_office: 'Savills IM Offices',
            map: 'Map',
            asset: 'Asset',
            office: 'Savills IM Office',  
            previous: 'Previous',
            next: 'Next',  
        },
        de: {
            all: 'Alle',
            example_asset: 'Objekte',
            savills_office: 'Savills IM Büros',
            map: 'Karte',
            asset: 'Objekt',
            office: 'Savills IM Büro',
            previous: 'Vorherige',
            next: 'Nächste',
        },
    };

    let mapCenter: [number, number] | null = null;

    switch(window.mapRegion) {
        default:
        case 'na':
            mapCenter = [-110.0060, 40.7128];
            break;

        case 'eu':
            mapCenter = [0, 45.541232];
            break;

        case 'apac':
            mapCenter = [99.6503, -15];
            break;
    }

    useEffect(() => {
        setActiveLocationIndex(0);
        setFilteredLocations(locations.filter(location => filter === 'all' || location.location_type === filter));        
    }, [filter, locations]);

    const nextLocation = (e) => {
        e.preventDefault();
        setActiveLocationIndex((activeLocationIndex + 1) % filteredLocations.length);
    }

    const prevLocation = (e) => {
        e.preventDefault();
        setActiveLocationIndex((activeLocationIndex - 1 + filteredLocations.length) % filteredLocations.length);
    }

    const activeLocation = useMemo(() => filteredLocations[activeLocationIndex], [filteredLocations, activeLocationIndex]);

    useEffect(() => {
        if (activeLocation && typeof window.gtag !== 'undefined') {
            window.gtag('event', 'map_regional_node_view', {
                'map_node': `${activeLocation.location_type === 'example_asset' ? 'Asset' : 'Savills IM Office'} - ${activeLocation.name}`,
            });
            console.log(`Map node view: ${activeLocation.location_type === 'example_asset' ? 'Asset' : 'Savills IM Office'} ${activeLocation.name}`);
        }
    }, [activeLocation]);

    const clickCta = (title, url) => {
        if (typeof window.gtag !== 'undefined') {
            window.gtag('event', 'map_regional_node_cta_click', {
                'map_node': `${activeLocation.location_type === 'example_asset' ? 'Asset' : 'Savills IM Office'} - ${activeLocation.name}`,
                'cta_title': title,
                'transport_type': 'beacon' // Ensures delivery even during navigation
            });
        }
        window.location = url;
    };

    const isMobile = window.innerWidth < 1024;

    const [zoom, setZoom] = useState(isMobile ? 4 : 3);
    const [position, setPosition] = useState(mapCenter);

    const handleFilter = ({ constructor: { name } }) => {
        return false;
    };

    // keydown left and right to navigate
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'ArrowRight') {
                nextLocation(event);
            } else if (event.key === 'ArrowLeft') {
                prevLocation(event);
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }
    , [activeLocationIndex, filteredLocations]);

    return (
        <div>
            <div className="flex flex-col gap-x-4 gap-y-6 lg:flex-row lg:items-center lg:justify-between mb-6 px-4 lg:px-8">
                <p className="text-savillsim-dark-blue max-w-lg">
                    {introText}
                </p>
                <div className="space-x-3">
                    <label className="space-x-1.5">
                        <input type="radio" name={filter} id="filter-all" checked={filter === 'all'} onChange={() => setFilter('all')} />
                        <span>{localeStrings[mapLanguage].all}</span>
                    </label>
                    <label className="space-x-1.5">
                        <input type="radio" name={filter} id="filter-example-asset" checked={filter === 'example_asset'} onChange={() => setFilter('example_asset')} />
                        <span className="pb-1 border-b-2 border-savillsim-teal">{localeStrings[mapLanguage].example_asset}</span>
                    </label>
                    <label className="space-x-1.5">
                        <input type="radio" name={filter} id="filter-savills-office" checked={filter === 'savills_office'} onChange={() => setFilter('savills_office')} />
                        <span className="pb-1 border-b-2 border-savillsim-orange">{localeStrings[mapLanguage].savills_office}</span>
                    </label>
                </div>
            </div>
            <div className="flex flex-col lg:grid grid-cols-1 lg:grid-cols-3 aspect-video border-t border-savillsim-dark-blue">
                <div className={classNames('h-full aspect-video overflow-hidden lg:col-start-1 lg:col-span-3 lg:row-start-1 border-b-[6px] lg:border-b-0 transition-all duration-500', { 'border-savillsim-teal': activeLocation.location_type === 'example_asset', 'border-savillsim-orange': activeLocation.location_type === 'savills_office' })}>
                    <ComposableMap projection="geoMercator" >
                        <ZoomableGroup 
                            zoom={zoom} 
                            center={position}
                            minZoom={isMobile ? 2 : 3}
                            maxZoom={isMobile ? 2 : 3}
                            filterZoomEvent={handleFilter}
                        >
                            <Geographies geography={geoUrl}>
                                {({ geographies }) =>
                                    geographies.map(geo => (
                                        <Geography
                                            key={geo.rsmKey}
                                            geography={geo}
                                            fill="#000A3C"
                                            stroke="#000A3C"
                                        />
                                    ))
                                }
                            </Geographies>
                            
                            {filteredLocations.map((location, index) => (
                                <CustomMarker 
                                    key={`${location.name}-${location.latLong.lat}-${location.latLong.long}`} 
                                    location={location} 
                                    isActive={activeLocation === location} 
                                    onClick={() => setActiveLocationIndex(index)} 
                                />
                            ))}
                        </ZoomableGroup>
                    </ComposableMap>
                </div>
                <div className="col-start-1 col-span-1 row-start-1 z-30 lg:px-8 lg:py-6 place-content-center">
                    <div className="bg-white h-auto">
                        <div className={classNames('hidden lg:block border-b-[6px] transition-all duration-500', { 'border-savillsim-teal': activeLocation.location_type === 'example_asset', 'border-savillsim-orange': activeLocation.location_type === 'savills_office' })}>
                            <img src={activeLocation.location_image.url} alt={activeLocation.name} className="aspect-video object-cover object-center" />
                        </div>
                        <div className="grow p-6 flex flex-col bg-white">
                            <h4 className="text-sm uppercase text-savillsim-dark-blue font-bold tracking-widest">
                                {activeLocation.location_type === 'example_asset' && <span>{localeStrings[mapLanguage].asset}</span>}
                                {activeLocation.location_type === 'savills_office' && <span>{localeStrings[mapLanguage].office}</span>}
                            </h4>
                            <h3 className="lg:-mt-1.5 h3 text-savillsim-dark-blue">{activeLocation.name}</h3>
                            <dl>
                                {(activeLocation.labels || []).map((label, index) => (
                                    <React.Fragment key={index}>
                                        <dt className="mt-3 text-sm uppercase text-savillsim-dark-blue font-bold tracking-widest">{label.label}</dt>
                                        <dd className="pb-3">{decodeHtmlEntities(label.value)}</dd>
                                    </React.Fragment>
                                ))}
                            </dl>

                            {activeLocation.location_link && (
                                <a onClick={() => clickCta(activeLocation.location_link.title, activeLocation.location_link.url)} className={`btn btn--transition mt-3 mb-6 btn--${activeLocation.location_type === 'example_asset' ? 'asset' : 'office'}`} href='#'>
                                    <span>{activeLocation.location_link.title}</span>
                                    <span className="hover">{activeLocation.location_link.title}</span>
                                </a>
                            )}

                            <div className="flex w-full items-center justify-between gap-3">
                                <a href="#" onClick={prevLocation} className="block border-[1.5px] px-4 py-3 text-savillsim-dark-blue transition border-savtext-savillsim-dark-blue hover:bg-savtext-savillsim-dark-blue hover:text-savillsim-dark-blue">
                                    <svg width="9" height="16" viewBox="0 0 9 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M7.5918 13.8072L1.50846 7.72201L7.5918 1.63867" stroke="currentColor" strokeWidth="2" style={{ strokeMiterlimit: 10 }} strokeLinecap="square"/>
                                    </svg>

                                    <div className="sr-only">{localeStrings[mapLanguage].previous}</div>
                                </a>
                                <span className="text-savillsim-dark-blue text-sm font-bold">
                                    {activeLocationIndex + 1} / {filteredLocations.length}
                                </span>
                                <a href="#" onClick={nextLocation} className="block border-[1.5px] px-4 py-3 text-savillsim-dark-blue transition border-savtext-savillsim-dark-blue hover:bg-savtext-savillsim-dark-blue hover:text-savillsim-dark-blue">
                                    <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1.5918 1.63813L7.67513 7.7233L1.5918 13.8066" stroke="currentColor" strokeWidth="2" style={{ strokeMiterlimit: 10 }} strokeLinecap="square"/>
                                    </svg>

                                    <div className="sr-only">{localeStrings[mapLanguage].next}</div>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

const container = document.getElementById('interactive-map');

if (container) {
    const root = createRoot(container);
    root.render(<App />);
}
